import { Entitlement } from '@collinsonx/utils';
import { useLazyQuery } from '@collinsonx/utils/apollo';
import { getEntitlementsForProduct } from '@collinsonx/utils/queries';
import { getItem, setItem } from '@lib';
import { useValuationService } from 'config/features';
import { ENTITLEMENTS } from 'config/storage';
import { Booking } from 'context/BookingContext';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import useEntitlements from './useEntitlements';
import usePayload from './usePayload';
import useProduct from './useProduct';

type useFetchEntitlementsProps = {
  booking: Booking;
};

const useFetchEntitlements = ({ booking }: useFetchEntitlementsProps) => {
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);

  const { fetchProductValuation, productID } = useProduct();
  const { linkedAccountId, membership, payload } = usePayload();
  const { hasPermissionsForEntitlements } = useEntitlements();

  const { departureDate } = booking;
  const { accountProvider } = payload || {};
  const programme = linkedAccountId ? accountProvider : null;

  const validateEntitlementsResponse = (data?: {
    getEntitlementsForProduct: Entitlement[];
  }) => {
    if (!data) return [];
    const entitlementsRes = data.getEntitlementsForProduct;

    if (!entitlementsRes || !entitlementsRes.length) return [];

    return entitlementsRes;
  };

  const [
    fetchEntitlements,
    { data: entitlementsData, loading: loadingEntitlements },
  ] = useLazyQuery<{
    getEntitlementsForProduct: Entitlement[];
  }>(getEntitlementsForProduct);

  const expiryDate = entitlements[0]?.expiryDate;
  const readyToFetchEntitlements = Boolean(
    departureDate &&
      membership?.id &&
      productID &&
      programme &&
      hasPermissionsForEntitlements
  );

  const entitlementsReturnValue =
    departureDate && expiryDate && dayjs(departureDate).isAfter(expiryDate)
      ? []
      : entitlements;

  const reloadEntitlements = async () => {
    if (!readyToFetchEntitlements) return;

    if (useValuationService) await fetchProductValuation();
    else
      await fetchEntitlements({
        variables: {
          bookingEnd: departureDate,
          membershipID: membership?.id,
          productId: productID,
          programme,
        },
      });
  };

  useEffect(() => {
    const cachedEntitlements = JSON.parse(getItem(ENTITLEMENTS) ?? 'false');
    if (!cachedEntitlements) return;

    setEntitlements(cachedEntitlements);
  }, []);

  useEffect(() => {
    if (useValuationService) return;
    if (!readyToFetchEntitlements || entitlements.length) return;

    fetchEntitlements({
      variables: {
        bookingEnd: departureDate,
        membershipID: membership?.id,
        productId: productID,
        programme,
      },
    });
  }, [readyToFetchEntitlements, departureDate]);

  useEffect(() => {
    const validatedEntitlements =
      validateEntitlementsResponse(entitlementsData);
    if (!validatedEntitlements.length) return;

    setItem(ENTITLEMENTS, JSON.stringify(validatedEntitlements));
    setEntitlements(validatedEntitlements);
  }, [entitlementsData]);

  return {
    entitlements: entitlementsReturnValue,
    fetchEntitlements,
    loadingEntitlements,
    readyToFetchEntitlements,
    reloadEntitlements,
  };
};

export default useFetchEntitlements;
